import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { handleApiError } from '../../services/api';
import { createApiUrlWithPrefix, getUserImageUrl } from '../../config/api';
import { useUserCounts } from '../../contexts/UserCountsContext';
import { toast } from 'react-hot-toast';
import {
  Search,
  UserPlus,
  Edit,
  Trash2,
  RefreshCw,
  Users,
  Eye,
  ChevronLeft,
  ChevronRight,
  Coins,
  Shield,
  MoreVertical,
  Printer
} from 'lucide-react';
import UserCreationModal from '../UserCreationModal';
import UserEditModal from '../UserEditModal';
import UserDetailsModal from '../UserDetailsModal';
import UserBadgeModal from '../UserBadgeModal';

function Temporary() {
  const { t, i18n } = useTranslation();
  const { refreshUserCounts } = useUserCounts();
  const isRTL = i18n.language === 'ar';

  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusUser, setStatusUser] = useState(null);
  const [userJetons, setUserJetons] = useState({}); // Store jeton data for each user
  const [dropdownOpen, setDropdownOpen] = useState(null); // Track which dropdown is open
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [badgeUser, setBadgeUser] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  

  // Fetch jeton data for a specific user
  const fetchUserJetons = useCallback(async (userId) => {
    try {
      const response = await axios.get(createApiUrlWithPrefix(`jeton/user/${userId}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data || [];
    } catch (error) {
      console.error(`Failed to fetch jetons for user ${userId}:`, error);
      return [];
    }
  }, []);

  // Fetch jeton data for all users
  const fetchAllUserJetons = useCallback(async (userList) => {
    const jetonData = {};
    for (const user of userList) {
      const jetons = await fetchUserJetons(user.id);
      jetonData[user.id] = jetons;
    }
    setUserJetons(jetonData);
  }, [fetchUserJetons]);

  // Fetch temporary users
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        role: '5', // Temporary role as number
        _t: Date.now(), // Cache busting parameter
        ...(searchTerm && { search: searchTerm })
      });

      const response = await axios.get(createApiUrlWithPrefix('users') + '?' + params, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const userList = response.data.users || [];
      setUsers(userList);
      setTotalPages(response.data.totalPages || 1);
      setTotalItems(response.data.totalItems || response.data.length);
      
      // Fetch jeton data for all users
      if (userList.length > 0) {
        await fetchAllUserJetons(userList);
      }
    } catch (error) {
      console.error('Failed to fetch temporary users:', error);
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, fetchAllUserJetons]);


  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.dropdown-container')) {
        setDropdownOpen(null);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);


  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleUserCreated = async () => {
    console.log('🔄 User created, refreshing list...');
    await fetchUsers(); // Refresh the list
    setRefreshKey(prev => prev + 1); // Force component refresh
    console.log('✅ Users list refreshed after creation');
  
    // Refresh sidebar counts
    refreshUserCounts();};

  const handleUserUpdated = () => {
    fetchUsers();
    setShowEditModal(false);
    setEditingUser(null);
    // Refresh sidebar counts (in case role changed)
    refreshUserCounts();
  };


  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  const handleViewUser = (user) => {
    setViewingUser(user);
    setShowDetailsModal(true);
  };

  const handleViewStatus = (user) => {
    setStatusUser(user);
    setShowStatusModal(true);
    setDropdownOpen(null);
  };

  const handleDropdownToggle = (userId) => {
    setDropdownOpen(dropdownOpen === userId ? null : userId);
  };

  const handleAction = (action, user) => {
    setDropdownOpen(null);
    switch (action) {
      case 'view':
        handleViewUser(user);
        break;
      case 'status':
        handleViewStatus(user);
        break;
      case 'edit':
        handleEditUser(user);
        break;
      case 'delete':
        handleDeleteUser(user.id);
        break;
      case 'print':
        setBadgeUser(user);
        setShowBadgeModal(true);
        break;
      default:
        break;
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedUsers(users.map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await axios.delete(createApiUrlWithPrefix(`users/${userId}`), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        toast.success('User deleted successfully');
        
        // Check if we need to go back a page after deletion
        const currentUsersCount = users.length;
        console.log('📄 Current users count:', currentUsersCount, 'Current page:', currentPage);
        
        if (currentUsersCount === 1 && currentPage > 1) {
          console.log('⬅️ Going back to previous page');
          const newPage = Math.max(currentPage - 1, 1);
          setCurrentPage(newPage);
          // Don't call fetchUsers here - let useEffect handle it when currentPage changes
        } else {
          // Only refresh immediately if we're not changing pages
          console.log('🔄 Refreshing users list after delete...');
          await fetchUsers();
        }
        
        // Force component refresh
        setRefreshKey(prev => prev + 1);
        console.log('✅ Users list refreshed after delete');
        
        // Refresh sidebar counts
        refreshUserCounts();
      } catch (error) {
        console.error('Failed to delete user:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        
        // Extract error message from various possible locations
        let errorMessage = 'Failed to delete user';
        
        if (error.response?.data) {
          const errorData = error.response.data;
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          } else if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        // Check for specific error types
        if (error.response?.status === 403) {
          toast.error('Access denied: You do not have permission to delete users. Admin or SuperAdmin role required.');
        } else if (error.response?.status === 401) {
          toast.error('Unauthorized: Please log in again.');
        } else if (error.response?.status === 404) {
          toast.error('User not found.');
        } else {
          toast.error(errorMessage);
        }
      }
    }
  };

  const getRoleName = (role) => {
    switch (role) {
      case 4: return 'SuperAdmin';
      case 3: return 'Admin';
      case 2: return 'Coach';
      case 1: return 'Owner';
      case 0: return 'Rider';
      case 5: return 'Temporary';
      default: return 'Unknown';
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 4: return 'bg-red-100 text-red-800';
      case 3: return 'bg-purple-100 text-purple-800';
      case 2: return 'bg-blue-100 text-blue-800';
      case 1: return 'bg-green-100 text-green-800';
      case 0: return 'bg-yellow-100 text-yellow-800';
      case 5: return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusDisplayName = (status) => {
    switch (status) {
      case 0: return 'Active';
      case 1: return 'Deactivated';
      default: return 'Active';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 0: return 'bg-green-100 text-green-800';
      case 1: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Calculate jeton status for a user
  const getUserJetonStatus = (userId) => {
    const jetons = userJetons[userId] || [];
    if (jetons.length === 0) {
      return {
        totalJetons: 0,
        remainingJetons: 0,
        usedJetons: 0,
        hasActiveJetons: false,
        statusText: 'No Jetons',
        statusColor: 'bg-gray-100 text-gray-800'
      };
    }

    const totalJetons = jetons.reduce((sum, jeton) => sum + jeton.quantity, 0);
    const remainingJetons = jetons.reduce((sum, jeton) => sum + (jeton.remainingQuantity || 0), 0);
    const usedJetons = totalJetons - remainingJetons;

    if (remainingJetons > 0) {
      return {
        totalJetons,
        remainingJetons,
        usedJetons,
        hasActiveJetons: true,
        statusText: `${remainingJetons} Jetons`,
        statusColor: 'bg-green-100 text-green-800'
      };
    } else {
      return {
        totalJetons,
        remainingJetons: 0,
        usedJetons,
        hasActiveJetons: false,
        statusText: 'No Jetons',
        statusColor: 'bg-gray-100 text-gray-800'
      };
    }
  };

  /* removed early loading return to prevent search bar unmounting 
if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  } 
*/

  return (
    <>
      <div className="flex flex-col min-h-full bg-neutral-50 pb-8" dir={isRTL ? 'rtl' : 'ltr'} key={refreshKey}>
      {/* Fixed Header Section */}
      <div className="px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('common.temporaryUsers')}</h1>
          <p className="mt-2 sm:mt-3 text-sm sm:text-base text-gray-600">
            {t('common.manageTemporaryUsers')}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowModal(true)}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {t('common.addTemporaryUser')}
          </button>
        </div>
      </div>


      {/* Search Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-4 sm:p-6 mt-6">
        <div className="flex flex-col lg:flex-row gap-4 items-center">
          {/* Search Input */}
          <div className="flex-1 w-full">
            <div className="relative">
              <Search className={`absolute top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 ${isRTL ? 'right-4' : 'left-4'}`} />
              <input
                type="text"
                placeholder={t('common.searchTemporaryUsers')}
                value={searchTerm}
                onChange={handleSearch}
                className={`w-full py-3 px-10 text-base sm:text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${isRTL ? 'pr-11 pl-4' : 'pl-11 pr-4'}`}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3 w-full lg:w-auto">
            <button
              onClick={clearFilters}
              className="flex-1 lg:flex-none justify-center px-6 py-3 text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium"
            >
              {t('common.clear')}
            </button>
            
            <button
              onClick={fetchUsers}
              className="flex-1 lg:flex-none flex justify-center items-center px-6 py-3 text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      {/* End of Fixed Header Section */}

      {/* Scrollable Cards Section */}
      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        {/* Select All and Users Cards */}
        <div className="space-y-6">
          {/* Select All Checkbox */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectedUsers.length === users.length && users.length > 0}
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-3"
              />
              <span className="text-sm font-medium text-gray-700">
                {t('common.selectAll')} ({selectedUsers.length} of {users.length} {t('common.selected')})
              </span>
            </div>
            {selectedUsers.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  {selectedUsers.length} {t('common.selected')}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Users Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          {users.map((user) => (
            <div key={user.id} className="relative bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-xl transition-all duration-300 overflow-hidden group">
              {/* Header with checkbox and role badge */}
              <div className="flex items-center justify-between p-4 pb-2">
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(user.id)}
                  onChange={() => handleSelectUser(user.id)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${getRoleBadgeColor(user.role)}`}>
                    {getRoleName(user.role)}
                  </span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${getStatusBadgeColor(user.status)}`}>
                    {t(`common.${getStatusDisplayName(user.status).toLowerCase()}`)}
                  </span>
                </div>
              </div>

              {/* User Info */}
              <div className="px-4 pb-4">
                {/* Avatar */}
                <div className="flex justify-center mb-4">
                  {getUserImageUrl(user) ? (
                    <div className="relative">
                      <img
                        src={getUserImageUrl(user)}
                        alt={user.name || 'Temporary User'}
                        className="w-16 h-16 rounded-full object-cover border-2 border-orange-100 shadow-sm"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center border-2 border-orange-100 shadow-sm hidden">
                        <span className="text-xl font-bold text-orange-600">
                          {user.name?.charAt(0)?.toUpperCase() || 'T'}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center border-2 border-orange-100 shadow-sm">
                      <span className="text-xl font-bold text-orange-600">
                        {user.name?.charAt(0)?.toUpperCase() || 'T'}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Name and Email */}
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">
                    {user.name || 'Unknown User'}
                  </h3>
                  <p className="text-sm text-gray-500 truncate">{user.email}</p>
                </div>
                
                {/* Key Info */}
                <div className="space-y-2 text-xs text-gray-600">
                  {user.phoneNumber && (
                    <div className="flex items-center justify-center">
                      <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-medium">
                        📞 {user.phoneNumber}
                      </span>
                    </div>
                  )}
                  
                  {user.birthDate && user.birthDate !== '0001-01-01T00:00:00' && (
                    <div className="flex items-center justify-center">
                      <span className="bg-orange-50 text-orange-700 px-3 py-1 rounded-full font-medium">
                        🎂 {new Date().getFullYear() - new Date(user.birthDate).getFullYear()} {t('common.years')}
                      </span>
                    </div>
                  )}
                  
                  {/* Jeton Status */}
                  {(() => {
                    const jetonStatus = getUserJetonStatus(user.id);
                    return (
                      <div className="flex items-center justify-center">
                        <span className={`px-3 py-1 rounded-full font-medium ${jetonStatus.statusColor}`}>
                          🪙 {jetonStatus.statusText.replace('Jetons', t('common.jetons')).replace('No Jetons', t('common.noJetons'))}
                        </span>
                      </div>
                    );
                  })()}
                  
                  <div className="flex items-center justify-center">
                    <span className="bg-orange-50 text-orange-700 px-3 py-1 rounded-full font-medium">
                      🏃 {t('common.temporaryUser')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Dropdown - Top Right */}
              <div className="absolute top-4 right-4 dropdown-container">
                <button
                  onClick={() => handleDropdownToggle(user.id)}
                  className="flex items-center justify-center w-8 h-8 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Actions"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
                
                {dropdownOpen === user.id && (
                  <div className="absolute right-0 top-10 z-[9999] w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                    <button
                      onClick={() => handleAction('view', user)}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <Eye className="w-4 h-4 mr-3" />
                      {t('common.view')}
                    </button>
                    <button
                      onClick={() => handleAction('status', user)}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <Shield className="w-4 h-4 mr-3" />
                      {t('common.status')}
                    </button>
                    <button
                      onClick={() => handleAction('edit', user)}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <Edit className="w-4 h-4 mr-3" />
                      {t('common.edit')}
                    </button>
                    <button
                      onClick={() => handleAction('print', user)}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <Printer className="w-4 h-4 mr-3" />
                      {t('common.printBadge') || 'Imprimer Badge'}
                    </button>
                    <button
                      onClick={() => handleAction('delete', user)}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="w-4 h-4 mr-3" />
                      {t('common.delete')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {users.length === 0 && !loading && (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('common.noTemporaryUsers')}</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm ? 'No users found matching your search.' : 'Get started by adding your first temporary user.'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => setShowModal(true)}
                className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
              >
                {t('common.addTemporaryUser')}
              </button>
            )}
          </div>
        )}
          </div>
        </div>
      </div>
      {/* End of Scrollable Cards Section */}

      {/* Pagination - Sticky at bottom */}
      <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
          <div className="text-xs sm:text-sm text-gray-700 font-medium">
            Showing <span className="font-bold text-blue-600">{((currentPage - 1) * itemsPerPage) + 1}</span> to <span className="font-bold text-blue-600">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of <span className="font-bold text-blue-600">{totalItems}</span> results
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || totalPages <= 1}
              className="flex items-center px-3 py-2 sm:px-4 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all duration-200 min-h-[44px] touch-manipulation"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Prev</span>
            </button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                if (pageNum > totalPages) return null;
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 min-h-[44px] min-w-[44px] touch-manipulation ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages <= 1}
              className="flex items-center px-3 py-2 sm:px-4 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all duration-200 min-h-[44px] touch-manipulation"
            >
              <span className="hidden sm:inline">Next</span>
              <span className="sm:hidden">Next</span>
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          </div>
        </div>
      </div>

      {/* User Creation Modal */}
      <UserCreationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onUserCreated={handleUserCreated}
        role="Temporary"
      />
      
      {/* User Edit Modal */}
      {editingUser && (
        <UserEditModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingUser(null);
          }}
          user={editingUser}
          onUserUpdated={handleUserUpdated}
          allowedRoles={[0, 5]} // Allow Temporary (5) to stay or change to Rider (0)
        />
      )}
      
      {/* User Details Modal */}
      {viewingUser && (
        <UserDetailsModal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setViewingUser(null);
          }}
          user={viewingUser}
        />
      )}

      {/* Temporary User Status Modal */}
      {showStatusModal && statusUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-gray-900">
                  {t('common.temporaryUserStatus')} - {statusUser.name || `${statusUser.firstName} ${statusUser.lastName}`}
                </h2>
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    setStatusUser(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6 p-4 sm:p-6 lg:p-8">
                {/* Jeton Status */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <Coins className="w-6 h-6 text-orange-600 mr-3" />
                    <h3 className="text-lg font-semibold text-orange-900">{t('common.jetonStatus')}</h3>
                  </div>
                  {(() => {
                    const jetonStatus = getUserJetonStatus(statusUser.id);
                    return (
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">{t('common.status')}:</span>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${jetonStatus.statusColor}`}>
                            {jetonStatus.statusText}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">{t('common.totalJetons')}:</span>
                          <span className="text-sm text-gray-900">{jetonStatus.totalJetons}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">{t('common.remainingJetons')}:</span>
                          <span className="text-sm text-gray-900">{jetonStatus.remainingJetons}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">{t('common.usedJetons')}:</span>
                          <span className="text-sm text-gray-900">{jetonStatus.usedJetons}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Jeton Details */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <Shield className="w-6 h-6 text-blue-600 mr-3" />
                    <h3 className="text-lg font-semibold text-blue-900">{t('common.jetonDetails')}</h3>
                  </div>
                  {(() => {
                    const jetons = userJetons[statusUser.id] || [];
                    if (jetons.length === 0) {
                      return (
                        <div className="text-center py-4">
                          <p className="text-gray-500">{t('common.noJetonsFound')}</p>
                        </div>
                      );
                    }

                    return (
                      <div className="space-y-4">
                        {jetons.map((jeton, index) => (
                          <div key={jeton.id || index} className="border rounded-lg p-4 bg-white">
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <h4 className="font-medium text-gray-900">
                                  {t('common.jetonPackage')} #{index + 1}
                                </h4>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    jeton.status === 0 ? 'bg-green-200 text-green-800' :
                                    jeton.status === 1 ? 'bg-red-200 text-red-800' :
                                    'bg-gray-200 text-gray-800'
                                  }`}>
                                    {jeton.status === 0 ? t('common.active') : 
                                     jeton.status === 1 ? t('common.used') : t('common.cancelled')}
                                  </span>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-medium text-gray-900">
                                  {jeton.quantity} {t('common.jetons')}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {jeton.pricePerJeton.toLocaleString()} DA {t('common.each')}
                                </div>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500">Total Amount:</span>
                                <div className="font-medium">{jeton.totalAmount.toLocaleString()} DA</div>
                              </div>
                              <div>
                                <span className="text-gray-500">Remaining:</span>
                                <div className="font-medium">{jeton.remainingQuantity || 0}</div>
                              </div>
                              <div>
                                <span className="text-gray-500">{t('common.purchaseDate')}:</span>
                                <div className="font-medium">
                                  {new Date(jeton.purchaseDate).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* User Information */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <Users className="w-6 h-6 text-gray-600 mr-3" />
                    <h3 className="text-lg font-semibold text-gray-900">User Information</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Name:</span>
                      <div className="font-medium">{statusUser.name || `${statusUser.firstName} ${statusUser.lastName}`}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Email:</span>
                      <div className="font-medium">{statusUser.email}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Phone:</span>
                      <div className="font-medium">{statusUser.phoneNumber || 'N/A'}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('common.status')}:</span>
                      <div className="font-medium">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          statusUser.status === 0 ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                        }`}>
                          {statusUser.status === 0 ? t('common.active') : t('common.deactivated')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Badge Modal */}
      <UserBadgeModal
        isOpen={showBadgeModal}
        onClose={() => {
          setShowBadgeModal(false);
          setBadgeUser(null);
        }}
        user={badgeUser}
      />
      </div>
      {/* End of main flex container */}
    </>
  );
}

export default Temporary;
